package learning.components

import antd.button.button
import antd.card.card
import antd.divider.divider
import antd.form.form
import antd.form.formItem
import antd.icon.editOutlined
import antd.icon.plusOutlined
import antd.input.input
import antd.message.message
import antd.modal.modal
import com.jingfangpai.common.data.type.DateTime
import com.jingfangpai.common.orm.mapping.Comment
import com.jingfangpai.common.orm.mapping.enums.CommentEntityType
import com.jingfangpai.common.orm.mapping.enums.CommentStatus
import com.jingfangpai.common.orm.mapping.enums.CommentType
import external.ckEditor.CKEditor
import external.ckEditor.ClassicEditor
import external.ckEditor.EditorConf
import external.ckEditor.EditorToolbarConf
import kotlinx.browser.document
import kotlinext.js.Object
import kotlinext.js.jsObject
import kotlinx.html.style
import org.w3c.dom.HTMLScriptElement
import react.*
import react.dom.div
import react.dom.jsStyle
import react.dom.span
import user.service.AuthService

external interface NotesSectionProps : RProps {
    var entityType: CommentEntityType
    var entityId: String
}

data class NotesSectionContextData(
    val getComments: (entityType: CommentEntityType, entityId: String, type: CommentType, okCallback: (result: com.jingfangpai.common.data.response.ApiResponse<Array<Comment>>) -> Unit, errorCallback: (exception: Throwable) -> Unit) -> Unit,
    val saveComment: (comment: Comment, okCallback: (result: com.jingfangpai.common.data.response.ApiResponse<Comment>) -> Unit, errorCallback: (exception: Throwable) -> Unit) -> Unit
)

val NotesSectionContext = createContext<NotesSectionContextData?>(null)

val NotesSection = functionalComponent<NotesSectionProps> { props ->
    val ctx = useContext(NotesSectionContext)
    val (notes, setNotes) = useState(arrayOf<Comment>())
    val (isLoading, setIsLoading) = useState(true)
    val (showEditModal, setShowEditModal) = useState(false)
    val (editingNote, setEditingNote) = useState<Comment?>(null)
    val (noteForm, setNoteForm) = useState(Comment())
    val (saving, setSaving) = useState(false)
    val (saveError, setSaveError) = useState<String?>(null)
    val (showDeleteModal, setShowDeleteModal) = useState(false)
    val (deletingNoteId, setDeletingNoteId) = useState<String?>(null)
    val (noteEditor, setNoteEditor) = useState<CKEditor?>(null)
    val (editorScriptLoaded, setEditorScriptLoaded) = useState(false)

    // CKEditor Base64 image upload adapter
    val configureBase64Upload = js("""
function(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = function(loader) {
        return {
            upload: function() {
                return loader.file.then(function(file) {
                    return new Promise(function(resolve) {
                        var reader = new FileReader();
                        reader.onload = function() { resolve({ 'default': reader.result }); };
                        reader.readAsDataURL(file);
                    });
                });
            }
        };
    };
}
""").unsafeCast<(dynamic) -> Unit>()

    // Load CKEditor script once (global singleton)
    useEffectOnce {
        if (js("typeof ClassicEditor !== 'undefined'").unsafeCast<Boolean>()) {
            setEditorScriptLoaded(true)
        } else if (document.getElementById("ckeditor-script-global") != null) {
            setEditorScriptLoaded(true)
        } else {
            val s = document.createElement("script") as HTMLScriptElement
            s.src = "/ckeditor.js"
            s.id = "ckeditor-script-global"
            document.body?.appendChild(s)
            s.onload = {
                setEditorScriptLoaded(true)
            }
        }
    }

    // Initialize / cleanup editor when modal visibility changes
    useEffect(showEditModal, editorScriptLoaded) {
        if (showEditModal && editorScriptLoaded) {
            js("setTimeout")({
                val el = document.querySelector("#note-editor-detail")
                if (el != null) {
                    val config = jsObject<EditorConf> {
                        toolbar = jsObject<EditorToolbarConf> {
                            items = arrayOf(
                                "bold", "italic", "underline",
                                "fontSize", "fontColor", "link",
                                "bulletedList", "numberedList",
                                "|",
                                "outdent", "indent",
                                "|",
                                "imageUpload", "blockQuote",
                                "undo", "redo"
                            )
                        }
                        language = "zh-cn"
                        licenseKey = ""
                    }
                    ClassicEditor.create(el, config)
                        .then { editor: dynamic ->
                            configureBase64Upload(editor)
                            setNoteEditor(editor.unsafeCast<CKEditor>())
                            noteForm.comment?.let { data ->
                                editor.setData(data)
                            }
                            editor.model.document.on("change:data") { _: dynamic, _: dynamic ->
                                val newComment = editor.getData() as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        comment = newComment
                                    }
                                }
                            }
                        }
                        .catch { error: dynamic ->
                            console.error("CKEditor init failed:", error)
                        }
                }
            }, 150)
        }
    }

    fun destroyNoteEditor() {
        noteEditor?.let { editor ->
            try {
                editor.asDynamic().destroy()
            } catch (e: dynamic) {
                console.log("Editor destroy error:", e)
            }
        }
        setNoteEditor(null)
        val el = document.querySelector("#note-editor-detail")
        if (el != null) {
            el.innerHTML = ""
        }
    }

    fun loadNotes() {
        ctx?.getComments?.invoke(
            props.entityType,
            props.entityId,
            CommentType.笔记,
            { resp ->
                if (resp.ok() && resp.data != null) {
                    setNotes(resp.data!!)
                }
                setIsLoading(false)
            },
            { error ->
                console.log(error)
                message.error("加载笔记失败")
                setIsLoading(false)
            }
        )
    }

    useEffectOnce {
        loadNotes()
    }

    fun handleSave() {
        val editorComment = noteEditor?.asDynamic()?.getData() as? String
        val commentToSave = editorComment ?: noteForm.comment

        if (commentToSave.isNullOrBlank()) {
            setSaveError("请输入笔记内容")
            return
        }

        setSaving(true)
        setSaveError(null)
        destroyNoteEditor()
        val noteToSave = Object.assign(Comment(), noteForm).apply {
            comment = commentToSave
            entityType = props.entityType
            entityId = props.entityId
            type = CommentType.笔记
            authorId = AuthService.getUserAuthData()?.id ?: ""
            createdBy = AuthService.getUserAuthData()?.id
            status = CommentStatus.正常
        }

        if (noteToSave.createdOn == null) {
            noteToSave.createdOn = DateTime()
        }

        ctx?.saveComment?.invoke(
            noteToSave,
            { resp ->
                if (resp.ok()) {
                    message.success("保存成功")
                    setShowEditModal(false)
                    setEditingNote(null)
                    setNoteForm(Comment())
                    loadNotes()
                } else {
                    message.error("保存失败：${resp.message}")
                }
                setSaving(false)
            },
            { error ->
                message.error("保存失败：${error.message}")
                setSaving(false)
            }
        )
    }

    fun handleDelete(noteId: String) {
        setDeletingNoteId(noteId)
        setShowDeleteModal(true)
    }

    fun confirmDelete() {
        deletingNoteId?.let { noteId ->
            val noteToDelete = notes.find { it.id == noteId }
            if (noteToDelete != null) {
                setSaving(true)
                val updatedNote = Object.assign(Comment(), noteToDelete).apply {
                    status = CommentStatus.已删除
                }
                ctx?.saveComment?.invoke(
                    updatedNote,
                    { resp ->
                        if (resp.ok()) {
                            message.success("删除成功")
                            loadNotes()
                        } else {
                            message.error("删除失败：${resp.message}")
                        }
                        setSaving(false)
                        setShowDeleteModal(false)
                        setDeletingNoteId(null)
                    },
                    { error ->
                        message.error("删除失败：${error.message}")
                        setSaving(false)
                        setShowDeleteModal(false)
                        setDeletingNoteId(null)
                    }
                )
            }
        }
    }

    fun openEditModal(note: Comment? = null) {
        setEditingNote(note)
        setSaveError(null)
        setNoteForm(note ?: Comment())
        setShowEditModal(true)
    }

    div("NotesSection") {
        card {
            val cardAttrs = attrs
            cardAttrs.title = buildElement {
                span {
                    +"笔记 (${notes.size})"
                }
            }
            cardAttrs.extra = buildElement {
                button {
                    attrs {
                        type = "primary"
                        icon = buildElement { plusOutlined {} }
                        onClick = { openEditModal() }
                    }
                    +"添加笔记"
                }
            }
            cardAttrs.style = kotlinext.js.js {
                marginBottom = "16px"
            }

            if (isLoading) {
                div { +"加载中..." }
            } else if (notes.isEmpty()) {
                div {
                    attrs.jsStyle = kotlinext.js.js {
                        textAlign = "center"
                        padding = "20px"
                        color = "#999"
                    }
                    +"暂无笔记，点击右上角添加"
                }
            } else {
                notes.forEach { note ->
                    child(NoteItem) {
                        attrs {
                            this.note = note
                            this.onEdit = { openEditModal(note) }
                            this.onDelete = { handleDelete(note.id ?: "") }
                        }
                    }
                    if (note !== notes.last()) {
                        divider {}
                    }
                }
            }
        }

        // Edit Modal
        modal {
            val modalAttrs = attrs
            modalAttrs.visible = showEditModal
            modalAttrs.title = if (editingNote != null) "编辑笔记" else "添加笔记"
            modalAttrs.onOk = { handleSave() }
            modalAttrs.onCancel = {
                destroyNoteEditor()
                setSaveError(null)
                setShowEditModal(false)
                setEditingNote(null)
                setNoteForm(Comment())
            }
            modalAttrs.okText = if (saving) "保存中..." else "保存"
            modalAttrs.cancelText = "取消"
            modalAttrs.confirmLoading = saving
            modalAttrs.maskClosable = false
            modalAttrs.width = 600

            form {
                formItem {
                    attrs {
                        label = "标题"
                    }
                    input {
                        attrs {
                            value = noteForm.title ?: ""
                            placeholder = "请输入笔记标题（可选）"
                            onChange = { event: dynamic ->
                                val newTitle = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        title = newTitle
                                    }
                                }
                            }

                        }
                    }
                }

                formItem {
                    attrs {
                        label = "笔记内容"
                    }
                    div {
                        attrs.jsStyle = kotlinext.js.js {
                            border = "1px solid #d9d9d9"
                            borderRadius = "4px"
                            minHeight = "300px"
                        }
                        attrs["id"] = "note-editor-detail"
                    }
                    if (!saveError.isNullOrBlank()) {
                        div {
                            attrs.jsStyle = kotlinext.js.js {
                                color = "#ff4d4f"
                                fontSize = "12px"
                                marginTop = "4px"
                            }
                            +saveError!!
                        }
                    }
                }

                formItem {
                    attrs {
                        label = "来源"
                    }
                    input {
                        attrs {
                            value = noteForm.source ?: ""
                            placeholder = "请填写笔记来源（可选）"
                            onChange = { event: dynamic ->
                                val newSource = event.target.value as String
                                setNoteForm { currentNote ->
                                    Object.assign(Comment(), currentNote).apply {
                                        source = newSource
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Delete Confirmation Modal
        modal {
            val modalAttrs = attrs
            modalAttrs.visible = showDeleteModal
            modalAttrs.title = "确认删除"
            modalAttrs.onOk = { confirmDelete() }
            modalAttrs.onCancel = {
                setShowDeleteModal(false)
                setDeletingNoteId(null)
            }
            modalAttrs.okText = "删除"
            modalAttrs.cancelText = "取消"
            modalAttrs.confirmLoading = saving

            div {
                +"确定要删除这条笔记吗？"
            }
        }
    }
}

external interface NoteItemProps : RProps {
    var note: Comment
    var onEdit: () -> Unit
    var onDelete: () -> Unit
}

val NoteItem = functionalComponent<NoteItemProps> { props ->
    div("NoteItem") {
        attrs.jsStyle = kotlinext.js.js {
            marginBottom = "12px"
        }

        // Header with author and date
        div {
            attrs.jsStyle = kotlinext.js.js {
                display = "flex"
                justifyContent = "space-between"
                alignItems = "center"
                marginBottom = "8px"
            }
            
            span {
                attrs.jsStyle = kotlinext.js.js {
                    color = "#666"
                    fontSize = "13px"
                }
                +(props.note.createdBy ?: "未知用户")
            }

            div {
                span {
                    attrs.jsStyle = kotlinext.js.js {
                        color = "#999"
                        fontSize = "12px"
                        marginRight = "12px"
                    }
                    +formatDateTime(props.note.createdOn)
                }

                button {
                    attrs {
                        type = "link"
                        size = "small"
                        icon = buildElement { editOutlined {} }
                        onClick = { props.onEdit() }
                    }
                    +"编辑"
                }

                button {
                    attrs {
                        type = "link"
                        size = "small"
                        onClick = { props.onDelete() }
                    }
                    +"删除"
                }
            }
        }

        // Title (if exists)
        if (!props.note.title.isNullOrBlank()) {
            div {
                attrs.jsStyle = kotlinext.js.js {
                    fontWeight = "bold"
                    fontSize = "15px"
                    marginBottom = "8px"
                    color = "#333"
                }
                +props.note.title!!
            }
        }

        // Content
        div {
            attrs.jsStyle = kotlinext.js.js {
                lineHeight = "1.6"
                fontSize = "14px"
                marginBottom = if (!props.note.source.isNullOrBlank()) "4px" else "0px"
            }
            attrs["dangerouslySetInnerHTML"] = kotlinext.js.js { __html = props.note.comment ?: "" }
        }

        // Source (if exists)
        if (!props.note.source.isNullOrBlank()) {
            div {
                attrs.jsStyle = kotlinext.js.js {
                    fontSize = "12px"
                    color = "#999"
                    marginTop = "4px"
                }
                +"来源：${props.note.source}"
            }
        }
    }
}

private fun formatDateTime(dateTime: DateTime?): String {
    return dateTime?.toDateTimeString() ?: ""
}
